import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { Input } from "#components/atoms/Input/Input";
import { InputDimensions } from "#components/atoms/InputDimensions/InputDimensions";
import { Text } from "#components/atoms/Typographies/Text";
import { Field } from "#components/molecules/Field/Field";
import { Modal } from "#components/molecules/Modal/Modal";
import { useUpdatePart } from "#modules/parts/view/useUpdatePart";
import { useUpdatePartDimensions } from "#modules/parts/view/useUpdatePartDimensions";
import { useUpdatePartType } from "#modules/parts/view/useUpdatePartType";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import {
  buildEditPartDimensionsDefaults,
  buildEditPartDimensionsPayload,
  EditPartFormSchema,
  type EditPartFormValues,
} from "#modules/quoteRequestLinesV2/domain/editPartFormSchema";
import { PartFormatSelect } from "#modules/quoteRequestLinesV2/view/molecules/PartFormatSelect";
import { DiscardPartChangesConfirmationModal } from "#modules/quoteRequestLinesV2/view/organisms/DiscardPartChangesConfirmationModal";
import { getMaterialDimensionsFieldsConfiguration, PartTypeSchema } from "shared";

type Props = {
  line: QuoteRequestLineDto;
  onClose: () => void;
};

export const EditPartModal = ({ line, onClose }: Props) => {
  const { t } = useTranslation();
  const part = line.part;

  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [discardChangesModalOpened, setDiscardChangesModalOpened] = useState(false);

  const { control, handleSubmit, setValue, formState } = useForm<EditPartFormValues>({
    defaultValues: {
      name: part.name,
      type: part.type,
      dimensions: buildEditPartDimensionsDefaults(part.type, part),
    },
    resolver: zodResolver(EditPartFormSchema),
  });

  const type = useWatch({ control, name: "type" });
  const dimensionsFieldsConfig = getMaterialDimensionsFieldsConfiguration(type);

  const hasDimensionError = dimensionsFieldsConfig.some(
    ({ key }) => formState.errors.dimensions?.[key],
  );

  const { updatePartAsync } = useUpdatePart(part.id);
  const { mutateAsync: updatePartTypeAsync } = useUpdatePartType({
    partId: part.id,
    quoteRequestLine: line,
  });
  const { updatePartDimensionsAsync } = useUpdatePartDimensions({
    partId: part.id,
    quoteRequestId: line.quoteRequestId,
    quoteRequestLine: line,
    layoutId: null,
  });

  const handleAttemptClose = () => {
    if (formState.isDirty) {
      setDiscardChangesModalOpened(true);
    } else {
      onClose();
    }
  };

  const onSubmit = async (values: EditPartFormValues) => {
    setServerError(null);
    setIsSaving(true);
    try {
      if (values.type && values.type !== part.type) {
        await updatePartTypeAsync({ newPartType: values.type, newPartSubType: null });
      }

      const dimensionsPayload = buildEditPartDimensionsPayload(values.type, values.dimensions);
      if (Object.keys(dimensionsPayload).length > 0) {
        await updatePartDimensionsAsync({
          id: part.id,
          data: { ...dimensionsPayload, quoteRequestLineId: line.id },
        });
      }

      if (values.name !== part.name) {
        await updatePartAsync({ id: part.id, payload: { name: values.name } });
      }

      onClose();
    } catch {
      setServerError(t("quoteRequestV2.editPartModal.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal
        opened
        onClose={handleAttemptClose}
        title={t("quoteRequestV2.editPartModal.title")}
        closeText={t("quoteRequestV2.editPartModal.cancel")}
        primaryText={t("quoteRequestV2.editPartModal.save")}
        onPrimary={handleSubmit(onSubmit)}
        primaryLoading={isSaving}
      >
        {serverError && (
          <Text size="small" color={colors.text.errorDefault}>
            {serverError}
          </Text>
        )}

        <Controller
          name="name"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label={t("quoteRequestV2.editPartModal.nameLabel")}
              error={fieldState.error ? t("common.requiredField") : undefined}
            >
              <Input
                value={field.value}
                onChange={field.onChange}
                error={Boolean(fieldState.error)}
                aria-label={t("quoteRequestV2.editPartModal.nameLabel")}
              />
            </Field>
          )}
        />

        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Field label={t("common.partFormat")}>
              <PartFormatSelect
                value={field.value}
                onConfirm={(nextType) => {
                  setValue("type", nextType, { shouldDirty: true });
                  setValue("dimensions", buildEditPartDimensionsDefaults(nextType, part), {
                    shouldDirty: true,
                  });
                }}
                currentType={part.type}
                disabled={part.type === PartTypeSchema.enum.ASSEMBLY}
              />
            </Field>
          )}
        />

        <Controller
          name="dimensions"
          control={control}
          render={({ field }) => (
            <Field
              label={t("quoteRequestV2.editPartModal.dimensionsLabel")}
              error={
                hasDimensionError
                  ? t("quoteRequestV2.editPartModal.dimensionsMustBePositive")
                  : undefined
              }
            >
              <InputDimensions
                dimensions={dimensionsFieldsConfig.map(({ key }) => key)}
                thickness={field.value.thickness}
                diameter={field.value.diameter}
                height={field.value.height}
                width={field.value.width}
                length={field.value.length}
                onThicknessChange={(value) => field.onChange({ ...field.value, thickness: value })}
                onDiameterChange={(value) => field.onChange({ ...field.value, diameter: value })}
                onHeightChange={(value) => field.onChange({ ...field.value, height: value })}
                onWidthChange={(value) => field.onChange({ ...field.value, width: value })}
                onLengthChange={(value) => field.onChange({ ...field.value, length: value })}
                error={hasDimensionError}
                clearable
              />
            </Field>
          )}
        />
      </Modal>

      <DiscardPartChangesConfirmationModal
        opened={discardChangesModalOpened}
        onClose={() => setDiscardChangesModalOpened(false)}
        onConfirm={() => {
          setDiscardChangesModalOpened(false);
          onClose();
        }}
      />
    </>
  );
};
